import React, { useState, useEffect, useMemo } from 'react';
import { SensorData } from '../types';
import { fetchUserData, fetchCustomerSettings, logDashboardVisit } from '../services/dataService';
import { PRODUCT_CATALOG } from '../services/productCatalog';
import SensorCard from './SensorCard';
import M3TRmonCard from './M3TRmonCard';
import SensorHintCard from './SensorHintCard';
import SettingsModal from './SettingsModal';
import { RefreshCw, LogOut, MonitorOff, Share2, Check, Settings } from 'lucide-react';

interface DashboardProps {
  userId: string;
  onLogout: () => void;
}

const MAX_HINTS = 2;
const POLL_INTERVAL_MS = 60 * 1000; // 1 min, and only while the tab is visible

const Dashboard: React.FC<DashboardProps> = ({ userId, onLogout }) => {
  const [sensors, setSensors] = useState<SensorData[]>([]);
  const [isLive, setIsLive] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [dismissedHints, setDismissedHints] = useState<string[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [customerName, setCustomerName] = useState<string | null>(null);

  const lastUpdate = useMemo(() => {
    const times = sensors
      .map(s => s.lastSeen ? new Date(s.lastSeen).getTime() : 0)
      .filter(t => t > 0);
    if (times.length === 0) return null;
    const mins = Math.floor((Date.now() - Math.max(...times)) / 60000);
    if (mins < 1) return 'hace un momento';
    if (mins < 60) return `hace ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `hace ${hrs} h`;
    return `hace ${Math.floor(hrs / 24)} d`;
  }, [sensors, refreshKey]);
  // Server-side switch; the per-browser dismissals still apply on top of it.
  const [hintsAllowed, setHintsAllowed] = useState(true);
  const [cutoffDay, setCutoffDay] = useState<number | null>(null);

  const hintsStorageKey = `dismissedHints_${userId}`;

  useEffect(() => {
    try {
      const stored = localStorage.getItem(hintsStorageKey);
      setDismissedHints(stored ? JSON.parse(stored) : []);
    } catch {
      setDismissedHints([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleDismissHint = (type: string) => {
    const updated = [...dismissedHints, type];
    setDismissedHints(updated);
    localStorage.setItem(hintsStorageKey, JSON.stringify(updated));
  };

  const visibleHints = useMemo(() => {
    const ownedTypes = new Set(sensors.map(s => s.type));
    return PRODUCT_CATALOG
      .filter(p => hintsAllowed && !ownedTypes.has(p.type) && !dismissedHints.includes(p.type))
      .slice(0, MAX_HINTS);
  }, [sensors, dismissedHints, hintsAllowed]);

  const loadData = async () => {
    setRefreshKey(k => k + 1);
    setLoading(true);
    try {
      const result = await fetchUserData(userId);
      setSensors(result.sensors);
      setIsLive(result.isLive);
      setCustomerName(result.customerName ?? null);
      setLoadError(result.error ?? null);
    } catch (e) {
      console.error(e);
      setLoadError('No pudimos cargar tus lecturas en este momento.');
    } finally {
      setLoading(false);
    }
  };

  // Also called directly by the header's refresh button — it has to live
  // outside the effect below for that to be possible. A plain function
  // reading current state via closure is fine here: it's recreated on every
  // render, same as loadData above, and the effect only ever uses the one
  // from its own render via the ref so it doesn't need it in its deps.
  const refreshNow = () => {
    loadData();
    setSecondsLeft(POLL_INTERVAL_MS / 1000);
  };
  const refreshNowRef = React.useRef(refreshNow);
  refreshNowRef.current = refreshNow;

  useEffect(() => {
    logDashboardVisit(userId);   // once per mount — not on the poll below
    fetchCustomerSettings(userId).then(st => { if (st) { setHintsAllowed(st.showProductHints); setCutoffDay(st.billingCutoffDay); } });

    // Was every 30s regardless of whether anyone was looking — a phone with
    // this tab left open overnight fired the full query set (~11 requests for
    // two sensors) well over a thousand times before morning, for nobody to
    // see. Now: only while the tab is the visible, active one, and paused —
    // not just hidden, stopped — the instant it isn't. Switching back in
    // fires one immediate refresh plus a fresh countdown, so the page never
    // feels stale on return despite having spent nothing while backgrounded.
    const refreshNow = () => refreshNowRef.current();

    let dataInterval: ReturnType<typeof setInterval> | null = null;
    let tickInterval: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (dataInterval) return;
      dataInterval = setInterval(refreshNow, POLL_INTERVAL_MS);
      tickInterval = setInterval(() => {
        setSecondsLeft(s => (s === null ? null : Math.max(0, s - 1)));
      }, 1000);
    };
    const stopPolling = () => {
      if (dataInterval) { clearInterval(dataInterval); dataInterval = null; }
      if (tickInterval) { clearInterval(tickInterval); tickInterval = null; }
      setSecondsLeft(null);   // no countdown shown while paused — nothing is counting down
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshNow();
        startPolling();
      } else {
        stopPolling();
      }
    };

    refreshNow();
    if (document.visibilityState === 'visible') startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading && sensors.length === 0) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="h-8 w-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Navbar */}
      <nav className="sticky top-0 z-20 bg-slate-900/90 backdrop-blur-md border-b border-slate-700 px-4 py-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
           <a href="https://m3tr.com" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src="/m3tr-logo.png" alt="M3TR" className="w-7 h-7 rounded-full" />
            <span className="font-bold text-lg text-slate-100">M3TR</span>
          </a>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={handleCopyLink}
            className="p-2 text-slate-400 hover:text-cyan-400 hover:bg-slate-800 rounded-full transition-colors relative"
            title="Copy unique link"
          >
            {copied ? <Check className="w-5 h-5 text-green-400" /> : <Share2 className="w-5 h-5" />}
          </button>
          <button onClick={onLogout} className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-full transition-colors">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="p-4 max-w-6xl mx-auto">
        <div className="mb-6 flex justify-between items-end">
          <div>
            <h1 className="text-2xl font-bold text-slate-100 mb-1">{customerName ?? 'Mis sensores'}</h1>
            {/* Says when the data was last measured, not whether the page
                reached the database — the customer assumes their data is
                theirs, and what they actually want to know is whether their
                equipment is still reporting. The old badge answered the
                developer's question, not the customer's. */}
            {loadError ? (
              <p className="flex items-center gap-1 text-sm text-red-400">
                <MonitorOff className="w-3.5 h-3.5" /> No pudimos cargar tus datos
              </p>
            ) : !isLive ? (
              <p className="flex items-center gap-1 text-sm text-orange-400">
                <MonitorOff className="w-3.5 h-3.5" /> Datos de muestra
              </p>
            ) : lastUpdate ? (
              <p className="text-sm text-slate-400">Última lectura {lastUpdate}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowSettings(true)}
              title="Configuración"
              className="p-2 bg-slate-800 rounded-full text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={refreshNow}
              title={secondsLeft !== null ? `Se actualiza en ${secondsLeft}s` : 'Actualizar'}
              className="p-2 bg-slate-800 rounded-full text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-colors relative"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              {/* Only shown while polling is actually running — nothing is
                  counting down while the tab is backgrounded, so no number
                  would be true to show. */}
              {secondsLeft !== null && (
                <span className="absolute -bottom-1 -right-1 bg-slate-700 text-slate-300 text-[9px] leading-none rounded-full w-4 h-4 flex items-center justify-center font-mono">
                  {secondsLeft}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Sensor Grid */}
        {sensors.length > 0 && (
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
            Your Sensors
          </h2>
        )}
        {loadError && (
          <div className="mb-4 p-4 rounded-xl border border-red-500/30 bg-red-500/5">
            <p className="text-red-300 text-sm font-medium">{loadError}</p>
            <p className="text-slate-400 text-xs mt-1">
              Your sensors keep recording either way — this is a problem showing the data, not collecting it.
            </p>
            <button
              onClick={refreshNow}
              disabled={loading}
              className="mt-3 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 disabled:opacity-50"
            >
              {loading ? 'Retrying…' : 'Try again'}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {sensors.length > 0 ? (
            sensors.map(sensor => (
              sensor.type === 'water'
                ? <M3TRmonCard key={sensor.id} data={sensor} refreshKey={refreshKey} billingCutoffDay={cutoffDay} />
                : <SensorCard key={sensor.id} data={sensor} />
            ))
          ) : (
             <div className={`text-center py-10 text-slate-500 text-sm border-2 border-dashed border-slate-800 rounded-xl ${loadError ? 'hidden' : ''}`}>
               No sensors found for this user.
             </div>
          )}
        </div>

        {/* Sensor Hints — subtle marketing for products the user doesn't own yet */}
        {visibleHints.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">
              Expand Your Setup
            </h2>
            <div className="space-y-3">
              {visibleHints.map(product => (
                <SensorHintCard key={product.type} product={product} onDismiss={handleDismissHint} />
              ))}
            </div>
          </div>
        )}
      {showSettings && (
          <SettingsModal
            secretId={userId}
            onClose={() => setShowSettings(false)}
            onSaved={() => { loadData(); fetchCustomerSettings(userId).then(st => { if (st) { setHintsAllowed(st.showProductHints); setCutoffDay(st.billingCutoffDay); } }); }}
          />
        )}

      </main>
    </div>
  );
};

export default Dashboard;